import { useEffect, useState } from "react";
import { useAtom } from "jotai";
import { useAccount } from "wagmi";
import { useQueryClient } from "@tanstack/react-query";

import { venueAtom } from "@/atoms";
import type { VenueId } from "@/config/contracts";
import { VENUES } from "@/config/contracts";
import {
  ajnaPositionAddressQueryKey,
  fetchAjnaPositionAddress,
} from "@/hooks/use-ajna-position-address";
import { FooterNote } from "@/components/footer-note";
import { VenueIconToggle } from "@/components/venue-icon-toggle";
import { Header } from "@/components/header";
import { LoopCard } from "@/components/loop-card";
import type { CardMode } from "@/components/mode-toggle";
import { PositionsPanel } from "@/components/positions-panel";
import { SupplyPage } from "@/components/supply-page";
import { ViewSwitch } from "@/components/view-switch";
import { usePathRoute } from "@/hooks/use-path-route";

export const App = () => {
  const { route, go } = usePathRoute();
  const [mode, setMode] = useState<CardMode>("open");
  const [expanded, setExpanded] = useState(false);
  const [venue, setVenue] = useAtom(venueAtom);
  const { address: account } = useAccount();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!account) return;
    const looper = VENUES.ajna.looper;
    void queryClient.prefetchQuery({
      queryKey: ajnaPositionAddressQueryKey(account, looper),
      queryFn: () => fetchAjnaPositionAddress(account, looper),
    });
  }, [account, queryClient]);

  // Modify/Close from a position lands on the fully-expanded builder for that
  // venue; a fresh "New position" starts collapsed.
  const goBuild = (nextMode: CardMode, targetVenue: VenueId) => {
    setMode(nextMode);
    setVenue(targetVenue);
    setExpanded(true);
    go({ section: "loop", loopView: "build" });
  };

  const goNew = () => {
    setMode("open");
    setExpanded(false);
    go({ section: "loop", loopView: "build" });
  };

  const isSupply = route.section === "supply";

  return (
    <>
      <Header
        section={route.section}
        onSection={(s) => go({ section: s })}
      />

      <main className="relative z-10 flex-1 px-6 md:px-12 pb-16 pt-6 md:pt-12">
        <div className="max-w-[1240px] mx-auto">
          <section className="mb-12 md:mb-16 max-w-[680px] fade-up">
            <h1
              className="text-[58px] md:text-[84px]"
              style={{ fontWeight: 300, lineHeight: 0.96 }}
            >
              {isSupply ? (
                <>
                  USDC yield,
                  <span style={{ color: "var(--ink-4)" }}> powered by the Loonie.</span>
                </>
              ) : (
                <>
                  Your Loonie
                  <span style={{ color: "var(--ink-4)" }}> with leverage.</span>
                </>
              )}
            </h1>
            <p
              className="mt-6 max-w-[520px] font-sans text-[15px]"
              style={{ color: "var(--ink-2)", fontWeight: 300, lineHeight: 1.55 }}
            >
              {isSupply
                ? "Supply USDC to the vault funding the CADC/USDC market. Earn the lending yield, withdraw anytime."
                : "Borrow USDC against your CADC, swap, redeposit. One transaction, repeated for leverage."}
            </p>
          </section>

          {isSupply ? (
            <SupplyPage />
          ) : (
            <>
              <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                {/* Mobile: venue toggle sits above the view switch, right-aligned.
                    Desktop: venue toggle is on the right, view switch on the left. */}
                <div
                  className="hidden md:block order-first md:order-last self-end"
                  style={{ visibility: route.loopView === "build" ? "visible" : "hidden" }}
                >
                  <VenueIconToggle value={venue} onChange={setVenue} />
                </div>
                <div className="order-last md:order-first md:w-auto">
                  <ViewSwitch
                    value={route.loopView}
                    onChange={(v) => (v === "build" ? goNew() : go({ loopView: v }))}
                  />
                </div>
              </div>

              {route.loopView === "positions" ? (
                <PositionsPanel
                  onModify={(venue) => goBuild("open", venue)}
                  onClose={(venue) => goBuild("close", venue)}
                  onNew={goNew}
                />
              ) : (
                <LoopCard
                  mode={mode}
                  onModeChange={setMode}
                  expanded={expanded}
                  onExpandedChange={setExpanded}
                />
              )}
            </>
          )}
        </div>
      </main>

      <FooterNote />
    </>
  );
};
